/**
 * CatalogAdminTab — admin-side view of the product catalog. Lists every
 * SKU grouped by category with inline price editing for sysadmin/admin.
 * Sales / other roles see a read-only browse view.
 *
 * Channels used
 *   catalog:list             — returns all SKUs (passes includeArchived:true)
 *   catalog:get-categories   — friendly group labels + counts
 *   catalog:upsert           — create or update a SKU (admin only — server-gated)
 *   catalog:archive          — soft-delete (is_active=FALSE)
 *
 * Design ref: docs/product_catalog_design_2026-06-06.md
 */
"use strict";

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Plus, Edit2, Archive, RefreshCw, Search, X, AlertCircle, Package, Inbox, Check, XCircle } from "lucide-react";

function fmtCents(cents) {
  if (!Number.isFinite(cents)) return "—";
  return "$" + (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const EMPTY_FORM = {
  sku: "",
  category: "",
  display_name: "",
  list_price_cents: 0,
  exosomes_included: 0,
  hotel_nights: 0,
  is_addon: false,
  display_order: 0,
  is_active: true,
  notes: "",
};

export default function CatalogAdminTab({ user }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null);   // SKU row or "new"
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saveMsg, setSaveMsg] = useState(null);

  const role = (user && user.role) || "other";
  const canEdit = role === "sysadmin" || role === "admin";
  // Managers / business / admin tier can approve override requests.
  const canApprove = ["sysadmin", "admin", "manager", "business"].includes(role);

  const [pendingRequests, setPendingRequests] = useState([]);
  const [pendingError, setPendingError] = useState(null);
  const [decisionBusy, setDecisionBusy] = useState(null); // request id mid-decision

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const api = window.zignagenixAPI;
      const [list, cats] = await Promise.all([
        api.invoke("catalog:list", { includeArchived: showArchived }),
        api.invoke("catalog:get-categories"),
      ]);
      setItems(Array.isArray(list) ? list : []);
      setCategories(Array.isArray(cats) ? cats : []);
      setLoading(false);
    } catch (e) {
      setError(e && e.message || "Failed to load catalog");
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Load pending approval requests on mount + when an action completes.
  const loadPending = useCallback(async () => {
    if (!canApprove) return;
    try {
      const rows = await window.zignagenixAPI.invoke("catalog:override-request:list-pending");
      setPendingRequests(Array.isArray(rows) ? rows : []);
      setPendingError(null);
    } catch (e) {
      setPendingError(e && e.message || "Failed to load pending approvals");
    }
  }, [canApprove]);
  useEffect(() => { loadPending(); }, [loadPending]);

  async function decideRequest(id, action, notes) {
    setDecisionBusy(id);
    try {
      const channel = action === "approve"
        ? "catalog:override-request:approve"
        : "catalog:override-request:reject";
      const r = await window.zignagenixAPI.invoke(channel, { id, notes: notes || null });
      if (r && r.error) {
        alert(r.message || `${action} failed`);
      } else {
        await loadPending();
      }
    } catch (e) {
      alert(e && e.message || `${action} failed`);
    } finally {
      setDecisionBusy(null);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(i => {
      if (filter !== "all" && i.category !== filter) return false;
      if (!q) return true;
      return (
        (i.display_name || "").toLowerCase().includes(q) ||
        (i.sku || "").toLowerCase().includes(q) ||
        (i.category || "").toLowerCase().includes(q) ||
        (i.notes || "").toLowerCase().includes(q)
      );
    });
  }, [items, search, filter]);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const i of filtered) {
      if (!m.has(i.category)) m.set(i.category, []);
      m.get(i.category).push(i);
    }
    // Preserve category order per the .display_order convention
    return Array.from(m.entries()).map(([cat, rows]) => ({
      category: cat,
      label: (categories.find(c => c.category === cat) || {}).label || cat,
      rows,
    }));
  }, [filtered, categories]);

  const openNew = () => {
    setEditing("new");
    setFormData({ ...EMPTY_FORM });
    setSaveMsg(null);
  };

  const openEdit = (row) => {
    setEditing(row.sku);
    setFormData({
      sku: row.sku,
      category: row.category || "",
      display_name: row.display_name || "",
      list_price_cents: Number(row.list_price_cents) || 0,
      exosomes_included: Number(row.exosomes_included) || 0,
      hotel_nights: Number(row.hotel_nights) || 0,
      is_addon: !!row.is_addon,
      display_order: Number(row.display_order) || 0,
      is_active: row.is_active !== false,
      notes: row.notes || "",
    });
    setSaveMsg(null);
  };

  const closeEditor = () => {
    setEditing(null);
    setFormData(EMPTY_FORM);
    setSaveMsg(null);
  };

  const handleSave = async () => {
    if (!formData.sku || !formData.category || !formData.display_name) {
      setSaveMsg({ kind: "error", text: "SKU, category, and display name are required." });
      return;
    }
    if (!Number.isFinite(formData.list_price_cents) || formData.list_price_cents < 0) {
      setSaveMsg({ kind: "error", text: "List price must be a non-negative integer (cents)." });
      return;
    }
    try {
      const r = await window.zignagenixAPI.invoke("catalog:upsert", formData);
      if (r && r.error) {
        setSaveMsg({ kind: "error", text: r.message || "Save failed." });
        return;
      }
      setSaveMsg({ kind: "ok", text: editing === "new" ? `Created ${formData.sku}` : `Updated ${formData.sku}` });
      await loadAll();
      setTimeout(closeEditor, 800);
    } catch (e) {
      setSaveMsg({ kind: "error", text: e && e.message || "Save failed." });
    }
  };

  const handleArchive = async (sku) => {
    if (!window.confirm(`Archive SKU ${sku}? Historical invoices keep referencing it; it just stops appearing in the picker.`)) return;
    try {
      const r = await window.zignagenixAPI.invoke("catalog:archive", sku);
      if (r && r.error) {
        alert(r.message || "Archive failed.");
        return;
      }
      await loadAll();
    } catch (e) {
      alert(e && e.message || "Archive failed.");
    }
  };

  const pageStyle = { padding: "8px 4px" };
  const cardStyle = { background: "#fff", border: "1px solid #DEE2E6", borderRadius: 12, marginBottom: 12 };
  const btn = { padding: "6px 12px", borderRadius: 8, border: "1px solid #DEE2E6", background: "#fff", fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 };
  const btnPrimary = { ...btn, background: "#2E8B98", color: "#fff", border: "none", fontWeight: 600 };

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Package size={20} color="#2E8B98" />
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#212529" }}>Product Catalog</h3>
            <p style={{ margin: "2px 0 0", fontSize: 11, color: "#6C757D" }}>
              {items.length} SKUs across {categories.length} categories
              {!canEdit && <span style={{ marginLeft: 8, color: "#B45309" }}>· read-only for your role ({role})</span>}
            </p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={btn} onClick={loadAll} type="button" disabled={loading}>
            <RefreshCw size={12} /> Refresh
          </button>
          {canEdit && (
            <button style={btnPrimary} onClick={openNew} type="button">
              <Plus size={14} /> New SKU
            </button>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{
          flex: "1 1 240px", minWidth: 200, display: "flex", alignItems: "center", gap: 6,
          background: "#fff", borderRadius: 10, padding: "6px 10px", border: "1px solid #DEE2E6",
        }}>
          <Search size={14} color="#6C757D" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, SKU, category, or notes"
            style={{ border: "none", outline: "none", flex: 1, fontSize: 13, background: "transparent" }}
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ background: "#fff", border: "1px solid #DEE2E6", borderRadius: 10, padding: "6px 8px", fontSize: 13 }}
        >
          <option value="all">All categories</option>
          {categories.map(c => <option key={c.category} value={c.category}>{c.label}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#495057" }}>
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Include archived
        </label>
      </div>

      {/* Status */}
      {loading && <p style={{ color: "#6C757D", fontSize: 13 }}>Loading catalog…</p>}
      {error && (
        <div style={{ background: "#FEE2E2", color: "#7F1D1D", padding: "8px 12px", borderRadius: 8, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {/* Pending approvals (managers / admins only) */}
      {canApprove && pendingRequests.length > 0 && (
        <div style={{ ...cardStyle, border: "1px solid #F5C26B", background: "#FFFBEB" }}>
          <div style={{
            padding: "10px 14px", borderBottom: "1px solid #F5C26B",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            background: "rgba(245, 194, 107, 0.12)",
            borderTopLeftRadius: 12, borderTopRightRadius: 12,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Inbox size={16} color="#B45309" />
              <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#7A4A00", textTransform: "uppercase", letterSpacing: 0.5 }}>
                Pending Override Approvals · {pendingRequests.length}
              </h4>
            </div>
            <button style={btn} onClick={loadPending} type="button"><RefreshCw size={11} /></button>
          </div>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "rgba(245, 194, 107, 0.06)", color: "#7A4A00", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
                <th style={{ textAlign: "left", padding: "8px 14px", fontWeight: 600 }}>Requested</th>
                <th style={{ textAlign: "left", padding: "8px 14px", fontWeight: 600 }}>By</th>
                <th style={{ textAlign: "left", padding: "8px 14px", fontWeight: 600 }}>Product</th>
                <th style={{ textAlign: "right", padding: "8px 14px", fontWeight: 600 }}>List → Req</th>
                <th style={{ textAlign: "left", padding: "8px 14px", fontWeight: 600 }}>Reason</th>
                <th style={{ textAlign: "right", padding: "8px 14px", fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingRequests.map(req => {
                const reqDate = req.requested_at ? new Date(req.requested_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
                const list = Number(req.list_price_cents);
                const reqUnit = Number(req.requested_unit_price_cents);
                const delta = reqUnit - list;
                return (
                  <tr key={req.id} style={{ borderTop: "1px solid rgba(245, 194, 107, 0.3)" }}>
                    <td style={{ padding: "8px 14px", fontSize: 11, color: "#7A4A00" }}>{reqDate}</td>
                    <td style={{ padding: "8px 14px" }}>
                      <span style={{ fontWeight: 500, color: "#212529" }}>{req.requested_by_name || `User ${req.requested_by_user_id}`}</span>
                      <span style={{ fontSize: 10, color: "#6C757D", marginLeft: 4 }}>({req.requested_by_role || "?"})</span>
                    </td>
                    <td style={{ padding: "8px 14px" }}>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{req.product_name || req.catalog_sku}</div>
                      <div style={{ fontSize: 10, fontFamily: "ui-monospace, monospace", color: "#6C757D" }}>{req.catalog_sku} × {req.quantity}</div>
                    </td>
                    <td style={{ padding: "8px 14px", textAlign: "right", fontSize: 12 }}>
                      <span style={{ textDecoration: "line-through", color: "#6C757D" }}>{fmtCents(list)}</span>
                      <span style={{ margin: "0 4px" }}>→</span>
                      <span style={{ fontWeight: 600, color: "#7A4A00" }}>{fmtCents(reqUnit)}</span>
                      <div style={{ fontSize: 10, color: delta < 0 ? "#0F766E" : "#B45309" }}>
                        ({delta < 0 ? "−" : "+"}{fmtCents(Math.abs(delta))})
                      </div>
                    </td>
                    <td style={{ padding: "8px 14px", fontSize: 11, color: "#495057", maxWidth: 200 }}>
                      {String(req.override_reason || "").slice(0, 80)}
                    </td>
                    <td style={{ padding: "8px 14px", textAlign: "right" }}>
                      <button
                        style={{ ...btn, background: "#DCFCE7", color: "#14532D", borderColor: "#86EFAC" }}
                        type="button"
                        onClick={() => decideRequest(req.id, "approve")}
                        disabled={decisionBusy === req.id}
                        title="Approve at requested price"
                      >
                        <Check size={11} /> Approve
                      </button>
                      <button
                        style={{ ...btn, background: "#FEE2E2", color: "#7F1D1D", borderColor: "#FCA5A5", marginLeft: 4 }}
                        type="button"
                        onClick={() => {
                          const notes = window.prompt("Rejection reason (optional):");
                          if (notes === null) return;  // user pressed Cancel
                          decideRequest(req.id, "reject", notes);
                        }}
                        disabled={decisionBusy === req.id}
                      >
                        <XCircle size={11} /> Reject
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {canApprove && pendingError && (
        <div style={{ background: "#FEE2E2", color: "#7F1D1D", padding: "8px 12px", borderRadius: 8, marginBottom: 12 }}>
          {pendingError}
        </div>
      )}

      {/* Grouped table */}
      {!loading && !error && grouped.map(g => (
        <div key={g.category} style={cardStyle}>
          <div style={{
            padding: "10px 14px", borderBottom: "1px solid #E9ECEF",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            background: "#F8F9FA", borderTopLeftRadius: 12, borderTopRightRadius: 12,
          }}>
            <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#2E8B98", textTransform: "uppercase", letterSpacing: 0.5 }}>
              {g.label}
            </h4>
            <span style={{ fontSize: 11, color: "#6C757D" }}>{g.rows.length} SKU{g.rows.length === 1 ? "" : "s"}</span>
          </div>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#FAFBFC", color: "#6C757D", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
                <th style={{ textAlign: "left", padding: "8px 14px", fontWeight: 600 }}>Product</th>
                <th style={{ textAlign: "left", padding: "8px 14px", fontWeight: 600 }}>SKU</th>
                <th style={{ textAlign: "right", padding: "8px 14px", fontWeight: 600 }}>List price</th>
                <th style={{ textAlign: "center", padding: "8px 14px", fontWeight: 600 }}>Exo</th>
                <th style={{ textAlign: "center", padding: "8px 14px", fontWeight: 600 }}>Hotel</th>
                <th style={{ textAlign: "center", padding: "8px 14px", fontWeight: 600 }}>Status</th>
                <th style={{ textAlign: "right", padding: "8px 14px", fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {g.rows.map(r => (
                <tr key={r.sku} style={{ borderTop: "1px solid #F1F3F5", opacity: r.is_active === false ? 0.55 : 1 }}>
                  <td style={{ padding: "8px 14px", color: "#212529", fontWeight: 500 }}>{r.display_name}</td>
                  <td style={{ padding: "8px 14px", color: "#495057", fontFamily: "ui-monospace, monospace", fontSize: 12 }}>{r.sku}</td>
                  <td style={{ padding: "8px 14px", textAlign: "right", color: "#2E8B98", fontWeight: 600 }}>{fmtCents(r.list_price_cents)}</td>
                  <td style={{ padding: "8px 14px", textAlign: "center", color: "#495057" }}>{r.exosomes_included || "—"}</td>
                  <td style={{ padding: "8px 14px", textAlign: "center", color: "#495057" }}>{r.hotel_nights || "—"}</td>
                  <td style={{ padding: "8px 14px", textAlign: "center", fontSize: 11 }}>
                    {r.is_active === false
                      ? <span style={{ color: "#7F1D1D", background: "#FEE2E2", padding: "2px 8px", borderRadius: 999 }}>archived</span>
                      : <span style={{ color: "#14532D", background: "#DCFCE7", padding: "2px 8px", borderRadius: 999 }}>active</span>}
                  </td>
                  <td style={{ padding: "8px 14px", textAlign: "right" }}>
                    {canEdit && (
                      <>
                        <button style={btn} type="button" onClick={() => openEdit(r)} title="Edit">
                          <Edit2 size={12} />
                        </button>
                        {r.is_active !== false && (
                          <button style={{ ...btn, marginLeft: 4 }} type="button" onClick={() => handleArchive(r.sku)} title="Archive">
                            <Archive size={12} />
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {/* Edit / new modal */}
      {editing && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) closeEditor(); }}
          style={{
            position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.55)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9000, padding: 20,
          }}
        >
          <div style={{
            background: "#F8F9FA", borderRadius: 14, padding: 0,
            width: "100%", maxWidth: 580,
            display: "flex", flexDirection: "column", overflow: "hidden",
            boxShadow: "0 18px 48px rgba(0,0,0,0.22)",
          }}>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid #E9ECEF", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#212529" }}>
                {editing === "new" ? "New catalog SKU" : `Edit ${editing}`}
              </h3>
              <button type="button" onClick={closeEditor} aria-label="Close" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                <X size={18} color="#6C757D" />
              </button>
            </div>

            <div style={{ padding: 18, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={{ gridColumn: "span 2" }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#495057", marginBottom: 4 }}>Display name *</label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  style={{ width: "100%", padding: "8px 10px", border: "1px solid #DEE2E6", borderRadius: 8, fontSize: 14, background: "#fff" }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#495057", marginBottom: 4 }}>SKU *</label>
                <input
                  type="text"
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value.toUpperCase().replace(/\s+/g, "_") })}
                  disabled={editing !== "new"}
                  style={{ width: "100%", padding: "8px 10px", border: "1px solid #DEE2E6", borderRadius: 8, fontSize: 14, fontFamily: "ui-monospace, monospace", background: editing !== "new" ? "#F1F3F5" : "#fff" }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#495057", marginBottom: 4 }}>Category *</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  list="catalog-category-options"
                  style={{ width: "100%", padding: "8px 10px", border: "1px solid #DEE2E6", borderRadius: 8, fontSize: 14, background: "#fff" }}
                />
                <datalist id="catalog-category-options">
                  {categories.map(c => <option key={c.category} value={c.category} />)}
                </datalist>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#495057", marginBottom: 4 }}>List price (cents) *</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={formData.list_price_cents}
                  onChange={(e) => setFormData({ ...formData, list_price_cents: parseInt(e.target.value, 10) || 0 })}
                  style={{ width: "100%", padding: "8px 10px", border: "1px solid #DEE2E6", borderRadius: 8, fontSize: 14, background: "#fff" }}
                />
                <p style={{ margin: "4px 0 0", fontSize: 10, color: "#6C757D" }}>{fmtCents(formData.list_price_cents)}</p>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#495057", marginBottom: 4 }}>Display order</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value, 10) || 0 })}
                  style={{ width: "100%", padding: "8px 10px", border: "1px solid #DEE2E6", borderRadius: 8, fontSize: 14, background: "#fff" }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#495057", marginBottom: 4 }}>Exosomes</label>
                <input
                  type="number"
                  min="0"
                  value={formData.exosomes_included}
                  onChange={(e) => setFormData({ ...formData, exosomes_included: parseInt(e.target.value, 10) || 0 })}
                  style={{ width: "100%", padding: "8px 10px", border: "1px solid #DEE2E6", borderRadius: 8, fontSize: 14, background: "#fff" }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#495057", marginBottom: 4 }}>Hotel nights</label>
                <input
                  type="number"
                  min="0"
                  value={formData.hotel_nights}
                  onChange={(e) => setFormData({ ...formData, hotel_nights: parseInt(e.target.value, 10) || 0 })}
                  style={{ width: "100%", padding: "8px 10px", border: "1px solid #DEE2E6", borderRadius: 8, fontSize: 14, background: "#fff" }}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 13, color: "#495057" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={!!formData.is_addon}
                    onChange={(e) => setFormData({ ...formData, is_addon: e.target.checked })}
                  /> is add-on
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={formData.is_active !== false}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  /> active
                </label>
              </div>
              <div style={{ gridColumn: "span 2" }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#495057", marginBottom: 4 }}>Notes</label>
                <textarea
                  rows={2}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  style={{ width: "100%", padding: "8px 10px", border: "1px solid #DEE2E6", borderRadius: 8, fontSize: 14, background: "#fff", fontFamily: "inherit" }}
                />
              </div>
            </div>

            {saveMsg && (
              <div style={{ margin: "0 18px 12px", padding: "8px 12px", borderRadius: 8, fontSize: 12, background: saveMsg.kind === "error" ? "#FEE2E2" : "#DCFCE7", color: saveMsg.kind === "error" ? "#7F1D1D" : "#14532D" }}>
                {saveMsg.text}
              </div>
            )}

            <div style={{ padding: "12px 18px", borderTop: "1px solid #E9ECEF", display: "flex", justifyContent: "flex-end", gap: 8, background: "#F1F3F5" }}>
              <button type="button" style={btn} onClick={closeEditor}>Cancel</button>
              <button type="button" style={btnPrimary} onClick={handleSave}>Save SKU</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
